<template>
  <!--
    The legacy big-photo banner hero is gated OUT of the new layout. The Figma
    new layout has NO photo banner — the profile card (<ChannelProfileCard>,
    mounted in <ChannelBodyV2>) leads the page. The member-list entry point that
    this hero used to own is now owned by the top bar in the new layout (which
    mounts its own LeaderboardModal), so `showMemberList` is unreachable here and
    the LeaderboardModal below stays gated `v-if="!newLayout"`. Legacy path (flag
    OFF) is byte-for-byte unchanged.
  -->
  <ChannelHero v-if="!newLayout" v-model:show-member-list="showMemberList" />

  <!--
    The member-list modal entry point is the hero's "members" button, which is
    hidden under enable_new_channel_layout (the top bar owns the member-list
    surface and mounts its OWN LeaderboardModal). So `showMemberList` is
    unreachable in the new layout — gate this instance off to avoid double-owning
    the modal. The legacy path is unchanged.
  -->
  <LeaderboardModal v-if="!newLayout" v-model:is-open="showMemberList" />

  <ChannelBodyLegacy v-if="!newLayout" :class="$attrs.class" />
  <ChannelBodyV2 v-else :class="$attrs.class" />

  <ReferralModal
    v-model:is-open="referralService.showReferralModal.value"
    :referral="referralService.currentReferralData.value"
    @invalid-id="onInvalidReferral"
    @close="clearReferralData"
    @completed-action="completedReferralAction"
  />

  <!--
    ANNOUNCEMENTS modal (new layout only). Hosted here (in the page body) because
    channel.vue owns the threads section / queries. Its open-state is the shared
    `useAnnouncementsModal` singleton, also opened by the top-bar ANNOUNCEMENTS
    tab (which lives in the shell, outside this tree). Gated to the new layout so
    the flag-OFF path is byte-for-byte unchanged.
  -->
  <ChannelAnnouncementsModal
    v-if="newLayout"
    v-model:is-open="announcementsModalOpen"
  />
</template>

<script setup lang="ts">
import { defineAsyncComponent, provide } from "vue";
import ChannelHero from "./channel-hero.vue";
import ChannelBodyLegacy from "./channel-body-legacy.vue";
import ChannelBodyV2 from "./channel-body-v2.vue";
import ChannelAnnouncementsModal from "./channel-page/channel-announcements-modal.vue";
import {
  useChannelPage,
  ChannelPageKey,
} from "@/composables/channel/use-channel-page";

// Lazy load
const ReferralModal = defineAsyncComponent(
  () => import("@/features/channel/referral/referral-modal.vue"),
);
const LeaderboardModal = defineAsyncComponent(
  () => import("@/components/leaderboard/leaderboard-modal.vue"),
);

// All page data / derived state / handlers live in the composable (run here so
// its lifecycle-bound composables bind to channel.vue's setup) and are provided
// to <ChannelBodyLegacy> / <ChannelBodyV2> via inject. channel.vue is a thin
// dispatcher: it picks the body for the current layout and hosts the shared
// modals.
const ctx = useChannelPage();
provide(ChannelPageKey, ctx);

const {
  newLayout,
  showMemberList,
  referralService,
  announcementsModalOpen,
  onInvalidReferral,
  clearReferralData,
  completedReferralAction,
} = ctx;
</script>
